import type { EmailCardTemplate } from '@/types';

/**
 * 邮件名片模板列表
 * 每个模板包含名称、描述以及用于生成 HTML 邮件内容的样式信息
 * 注意：邮箱客户端对 CSS 支持有限，模板全部使用内联样式、表格布局，
 * 不使用 flex、grid、外部字体等高级特性，保证在 Gmail、QQ、163、126 等主流邮箱中渲染一致。
 */
export const CARD_TEMPLATES: EmailCardTemplate[] = [
  {
    id: 'classic',
    name: '经典商务蓝',
    description: '深蓝渐变 + 白色文字，正式稳重的商务风格',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    accent: '#60a5fa',
    color: '#ffffff',
    icon: 'shield',
  },
  {
    id: 'midnight',
    name: '午夜紫罗兰',
    description: '紫黑渐变 + 霓虹紫强调色，神秘感十足',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #6d28d9 100%)',
    accent: '#c4b5fd',
    color: '#ffffff',
    icon: 'sparkles',
  },
  {
    id: 'sunset',
    name: '日落暖橙',
    description: '橙红渐变 + 米色文字，温暖醒目',
    background: 'linear-gradient(135deg, #c2410c 0%, #f59e0b 100%)',
    accent: '#fef3c7',
    color: '#fffbeb',
    icon: 'star',
  },
  {
    id: 'forest',
    name: '森林青玉',
    description: '深绿渐变 + 淡绿文字，自然清新',
    background: 'linear-gradient(135deg, #064e3b 0%, #10b981 100%)',
    accent: '#a7f3d0',
    color: '#ecfdf5',
    icon: 'lock',
  },
  {
    id: 'rose',
    name: '玫瑰金粉',
    description: '粉红渐变 + 玫瑰金文字，优雅柔美',
    background: 'linear-gradient(135deg, #9f1239 0%, #fb7185 100%)',
    accent: '#fda4af',
    color: '#fff1f2',
    icon: 'mail',
  },
  {
    id: 'carbon',
    name: '暗夜碳黑',
    description: '碳灰渐变 + 青色高亮，极简科技风',
    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
    accent: '#22d3ee',
    color: '#e2e8f0',
    icon: 'key',
  },
  {
    id: 'aurora',
    name: '极光幻彩',
    description: '蓝紫青绿渐变，北欧极光韵味',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 50%, #14b8a6 100%)',
    accent: '#a5f3fc',
    color: '#f0f9ff',
    icon: 'sparkles',
  },
  {
    id: 'gold',
    name: '尊贵流金',
    description: '黑金配色，沉稳大气，适合金融/高端应用',
    background: 'linear-gradient(135deg, #1c1917 0%, #78350f 100%)',
    accent: '#fcd34d',
    color: '#fef3c7',
    icon: 'star',
  },
];

/** 通过 id 获取模板 */
export function getCardTemplate(id: string): EmailCardTemplate {
  return CARD_TEMPLATES.find(t => t.id === id) || CARD_TEMPLATES[0];
}

/** 根据已启用列表与「随机」策略返回一个具体模板 */
export function pickCardTemplate(app: {
  card_template?: string;
  card_templates_enabled?: string[];
}): EmailCardTemplate {
  const enabledIds = (app.card_templates_enabled || []).filter(Boolean);
  const pool = enabledIds.length
    ? CARD_TEMPLATES.filter(t => enabledIds.includes(t.id))
    : CARD_TEMPLATES;
  if (!app.card_template || app.card_template === 'random') {
    return pool[Math.floor(Math.random() * pool.length)];
  }
  const matched = pool.find(t => t.id === app.card_template);
  return matched || pool[0];
}

/** 将模板 HTML 中的占位符替换为实际值 */
function fillPlaceholders(html: string, params: { code: string; appName: string; minutes: number }): string {
  return html
    .replaceAll('{code}', params.code)
    .replaceAll('{app_name}', params.appName)
    .replaceAll('{minutes}', String(params.minutes));
}

/**
 * 根据模板和参数生成验证码邮件的 HTML 内容（名片效果）。
 * 该 HTML 同时被后端用于发送、被前端用于实时预览。
 */
export function renderCardEmailHtml(
  template: EmailCardTemplate,
  params: { code: string; appName: string; minutes: number },
): string {
  // 邮件客户端兼容的内联样式 + 表格布局
  const year = new Date().getFullYear();
  const html = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>验证码</title>
</head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','PingFang SC','Hiragino Sans GB','Microsoft YaHei',sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f1f5f9;padding:32px 12px;">
    <tr>
      <td align="center">
        <table width="420" cellpadding="0" cellspacing="0" border="0" style="width:420px;max-width:420px;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 10px 30px rgba(15,23,42,0.12);">
          <!-- 名片头部 -->
          <tr>
            <td style="padding:0;">
              <div style="background:${template.background};padding:28px 28px 26px 28px;color:${template.color};border-radius:16px 16px 0 0;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="vertical-align:middle;">
                      <div style="font-size:12px;letter-spacing:3px;opacity:0.85;text-transform:uppercase;">SECURITY CODE</div>
                      <div style="font-size:20px;font-weight:600;margin-top:6px;">{app_name}</div>
                    </td>
                    <td align="right" style="vertical-align:middle;">
                      <div style="width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.18);text-align:center;line-height:44px;font-size:22px;">&#128274;</div>
                    </td>
                  </tr>
                </table>
              </div>
            </td>
          </tr>
          <!-- 名片主体 -->
          <tr>
            <td style="padding:32px 28px 16px 28px;">
              <div style="color:#475569;font-size:14px;line-height:22px;">您好，您正在进行身份验证，请使用以下验证码完成操作：</div>
              <div style="margin:22px 0 22px 0;text-align:center;">
                <div style="display:inline-block;background:${template.background};color:${template.color};font-size:34px;font-weight:700;letter-spacing:10px;padding:16px 28px;border-radius:12px;font-family:'Courier New',Courier,monospace;">{code}</div>
              </div>
              <div style="color:#64748b;font-size:13px;line-height:20px;text-align:center;">此验证码 {minutes} 分钟内有效，请勿向任何人泄露。</div>
            </td>
          </tr>
          <!-- 装饰分隔线 -->
          <tr>
            <td style="padding:0 28px;">
              <div style="height:1px;background:linear-gradient(90deg, transparent, ${template.accent}, transparent);opacity:0.6;"></div>
            </td>
          </tr>
          <!-- 名片底部 -->
          <tr>
            <td style="padding:18px 28px 28px 28px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="vertical-align:middle;">
                    <div style="color:${template.accent};font-size:12px;font-weight:600;letter-spacing:1px;">{app_name} · 安全中心</div>
                    <div style="color:#94a3b8;font-size:11px;margin-top:4px;">&copy; ${year} {app_name}. All rights reserved.</div>
                  </td>
                  <td align="right" style="vertical-align:middle;">
                    <div style="color:#94a3b8;font-size:11px;">此为系统邮件，请勿回复</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        <div style="color:#94a3b8;font-size:11px;text-align:center;padding:16px 0 0 0;">本邮件由 {app_name} 安全系统自动发送</div>
      </td>
    </tr>
  </table>
</body>
</html>`;
  return fillPlaceholders(html, params).trim();
}

/** 随机返回一个模板并生成 HTML（便于预览试看） */
export function renderRandomCardEmailHtml(params: { code: string; appName: string; minutes: number }): { template: EmailCardTemplate; html: string } {
  const template = CARD_TEMPLATES[Math.floor(Math.random() * CARD_TEMPLATES.length)];
  return { template, html: renderCardEmailHtml(template, params) };
}
